export const API_BASE_URL = '/api/v1';

export class ApiService {
  constructor() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    } else {
      localStorage.removeItem('accessToken');
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    } else {
      localStorage.removeItem('refreshToken');
    }
  }

  getHeaders(includeAuth = true, includeContentType = true) {
    const headers = {};
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    if (includeAuth && this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    return headers;
  }

  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to refresh token');
      }

      this.setTokens(data.accessToken, this.refreshToken);

      this.failedQueue.forEach(({ resolve }) => resolve());
      this.failedQueue = [];

      return data.accessToken;
    } catch (error) {
      this.failedQueue.forEach(({ reject }) => reject(error));
      this.failedQueue = [];

      this.setTokens(null, null);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  async fetchWithAuthRetry(url, config, endpoint, requireAuth = true) {
    let response = await fetch(url, config);

    if (response.status === 401 && requireAuth && endpoint !== '/auth/refresh') {
      try {
        await this.refreshAccessToken();
        response = await fetch(url, {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${this.accessToken}`,
          },
        });
      } catch (refreshError) {
        throw new Error('Session expired. Please login again.');
      }
    }

    return response;
  }

  async parseJsonResponse(response) {
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    return isJson ? await response.json() : null;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const requireAuth = options.requireAuth !== false;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(requireAuth),
        ...options.headers,
      },
    };

    try {
      const response = await this.fetchWithAuthRetry(url, config, endpoint, requireAuth);
      const data = await this.parseJsonResponse(response);

      if (!response.ok) {
        if (data) {
          const error = new Error(data.message || `HTTP error! status: ${response.status}`);
          error.status = response.status;
          error.code = data.code;
          throw error;
        }
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      return data !== null ? data : response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async register(username, password, captchaToken) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password, captchaToken }),
      requireAuth: false,
    });
  }

  async login(username, password, captchaToken) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, captchaToken }),
      requireAuth: false,
    });
    if (data.accessToken && data.refreshToken) {
      this.setTokens(data.accessToken, data.refreshToken);
    }
    return data;
  }

  async refresh() {
    return this.refreshAccessToken();
  }

  async logout() {
    if (this.refreshToken) {
      try {
        await this.request('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken: this.refreshToken }),
          requireAuth: false,
        });
      } catch (error) {
        console.error('Logout request failed:', error);
      }
    }
    this.setTokens(null, null);
  }

  async getProfile() {
    return this.request('/users/profile', {
      method: 'GET',
    });
  }
}

const api = new ApiService();
export default api;
